import { useEffect, useRef } from "react";

import { onCollectionChanged } from "./collectionEvents";

/**
 * Re-read a number whenever something changed the collection.
 *
 * Sits beside `useRefreshOnFocus` and does the other half of the job: that one covers "you were
 * away and studied elsewhere", this one covers "a job just finished under you". Most callers want
 * both, and they do not interfere — each holds its own bookkeeping.
 *
 * **Deliberately not throttled, unlike `useRefreshOnFocus`.** That hook's 5-second window exists
 * because `focus` and `visibilitychange` both fire on a single tab switch, so it is de-duplicating
 * an artefact of how the browser reports one event. A job finishing is not an artefact — it is a
 * fact, and a job that ends two seconds after you focused the tab still has to move the numbers.
 *
 * `read` is called with no arguments and its result ignored; it owns its own error handling, since
 * every caller would rather keep the last known number than replace it with a zero.
 */
export function useCollectionChanged(read: () => void): void {
  // Same reason as `useRefreshOnFocus`: the effect must not re-subscribe when the caller passes a
  // fresh closure, and the ref keeps the latest one reachable without that.
  const latest = useRef(read);
  latest.current = read;

  useEffect(() => onCollectionChanged(() => latest.current()), []);
}
